/**
 * Document Library Page
 * Sprint 2 / Sprint 11A: Updated with Layout component and Avida branding
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentsAPI, documentTypesAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Upload, RefreshCw, AlertCircle, Download, Archive, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['Company', 'Client', 'Personnel'];

function DocumentLibraryPage() {
  const navigate = useNavigate();
  const { user, logout, canManageDocuments, loading: authLoading } = useAuth();
  const fileInputRef = useRef(null);
  
  const [documents, setDocuments] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Active');
  
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [uploadData, setUploadData] = useState({
    document_type_code: '', linked_entity_type: 'Company', linked_entity_id: 'COMPANY',
    expires_at: '', notes: '', file: null
  });
  const [archiveReason, setArchiveReason] = useState('');

  const fetchDocTypes = async () => {
    try {
      const response = await documentTypesAPI.list({ is_active: true });
      setDocTypes(response.data.document_types);
    } catch (err) {
      console.error('Failed to load document types');
    }
  };

  const fetchDocuments = async () => {
    try {
      const params = { page, page_size: 50 };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (typeFilter !== 'all') params.document_type_code = typeFilter;
      if (entityTypeFilter !== 'all') params.linked_entity_type = entityTypeFilter;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      
      const response = await documentsAPI.list(params);
      setDocuments(response.data.documents);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    fetchDocTypes();
  }, [authLoading]);

  useEffect(() => {
    if (authLoading) return;
    fetchDocuments();
  }, [authLoading, page, categoryFilter, typeFilter, entityTypeFilter, statusFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDocuments();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadData({ ...uploadData, file: e.target.files[0] });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('document_type_code', uploadData.document_type_code);
      formData.append('linked_entity_type', uploadData.linked_entity_type);
      formData.append('linked_entity_id', uploadData.linked_entity_id);
      if (uploadData.expires_at) formData.append('expires_at', new Date(uploadData.expires_at).toISOString());
      if (uploadData.notes) formData.append('notes', uploadData.notes);
      
      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully');
      setUploadModalOpen(false);
      setUploadData({ document_type_code: '', linked_entity_type: 'Company', linked_entity_id: 'COMPANY', expires_at: '', notes: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to upload document';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handleArchive = async () => {
    if (archiveReason.length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await documentsAPI.archive(selectedDoc.id, archiveReason);
      toast.success('Document archived');
      setArchiveModalOpen(false);
      setArchiveReason('');
      setSelectedDoc(null);
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to archive document');
    } finally {
      setSubmitting(false);
    }
  };

  const openArchiveModal = (doc) => {
    setSelectedDoc(doc);
    setArchiveReason('');
    setArchiveModalOpen(true);
  };

  const openDetailModal = (doc) => {
    setSelectedDoc(doc);
    setDetailModalOpen(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getCategoryColor = (cat) => {
    const colors = {
      Company: 'bg-blue-100 text-blue-700 border-blue-200',
      Client: 'bg-green-100 text-green-700 border-green-200',
      Personnel: 'bg-purple-100 text-purple-700 border-purple-200',
    };
    return colors[cat] || 'bg-slate-100 text-slate-700';
  };

  const filteredDocTypes = docTypes.filter(dt => 
    uploadData.linked_entity_type === 'all' || dt.category === uploadData.linked_entity_type
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  const totalPages = Math.ceil(total / 50);

  return (
    <Layout title="Document Library">
      <div data-testid="document-library-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center space-x-4 flex-wrap gap-2">
            {canManageDocuments && (
              <Button className="bg-avida-blue hover:bg-avida-blue/90" onClick={() => setUploadModalOpen(true)} data-testid="upload-doc-btn">
                <Upload className="h-4 w-4 mr-2" />Upload Document
              </Button>
            )}
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger className="w-36" data-testid="entity-type-filter">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={refreshing ? 'h-4 w-4 mr-1 animate-spin' : 'h-4 w-4 mr-1'} />Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Documents ({total})</CardTitle>
            <CardDescription>All uploaded compliance documents</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Document</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Entity</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Version</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Uploaded</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => (
                        <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`doc-row-${doc.id}`}>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <div>
                                <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">{doc.file_name}</p>
                                <p className="text-xs text-slate-500">{formatFileSize(doc.file_size)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-slate-900">{doc.document_type_name || doc.document_type_code}</p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className={getCategoryColor(doc.linked_entity_type)}>
                              {doc.linked_entity_type}
                            </Badge>
                            <p className="text-xs text-slate-500 mt-1 truncate max-w-[100px]">{doc.linked_entity_id}</p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">v{doc.version}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-slate-600">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                            <p className="text-xs text-slate-400">{doc.uploaded_by_email}</p>
                          </td>
                          <td className="py-3 px-4">
                            {doc.status === 'Active' ? (
                              doc.is_expired ? (
                                <Badge variant="outline" className="bg-red-100 text-red-700">Expired</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-green-100 text-green-700">Active</Badge>
                              )
                            ) : (
                              <Badge variant="outline" className="bg-slate-100 text-slate-600">Archived</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-1">
                              <Button variant="ghost" size="sm" onClick={() => openDetailModal(doc)} data-testid={`view-${doc.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`}>
                                <Download className="h-4 w-4" />
                              </Button>
                              {canManageDocuments && doc.status === 'Active' && (
                                <Button variant="ghost" size="sm" onClick={() => openArchiveModal(doc)} className="text-red-600" data-testid={`archive-${doc.id}`}>
                                  <Archive className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {documents.length === 0 && (
                        <tr><td colSpan={7} className="py-8 text-center text-slate-500">No documents found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload a new document to the library</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Entity Type</Label>
                <Select value={uploadData.linked_entity_type} onValueChange={(v) => setUploadData({...uploadData, linked_entity_type: v, linked_entity_id: v === 'Company' ? 'COMPANY' : '', document_type_code: ''})}>
                  <SelectTrigger data-testid="upload-entity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Entity ID</Label>
                <Input value={uploadData.linked_entity_id} onChange={(e) => setUploadData({...uploadData, linked_entity_id: e.target.value})}
                  required placeholder={uploadData.linked_entity_type === 'Company' ? 'COMPANY' : 'Enter entity ID...'} 
                  disabled={uploadData.linked_entity_type === 'Company'} data-testid="upload-entity-id" />
              </div>
              <div>
                <Label>Document Type</Label>
                <Select value={uploadData.document_type_code} onValueChange={(v) => setUploadData({...uploadData, document_type_code: v})}>
                  <SelectTrigger data-testid="upload-doc-type"><SelectValue placeholder="Select document type..." /></SelectTrigger>
                  <SelectContent>
                    {filteredDocTypes.map(dt => <SelectItem key={dt.code} value={dt.code}>{dt.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>File</Label>
                <Input type="file" onChange={handleFileSelect} ref={fileInputRef} required data-testid="upload-file" />
              </div>
              <div>
                <Label>Expiration Date (optional)</Label>
                <Input type="date" value={uploadData.expires_at} onChange={(e) => setUploadData({...uploadData, expires_at: e.target.value})} data-testid="upload-expires" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={uploadData.notes} onChange={(e) => setUploadData({...uploadData, notes: e.target.value})} rows={2} data-testid="upload-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadModalOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting || !uploadData.document_type_code} data-testid="upload-submit">
                {submitting ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Document Details</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-slate-500">File Name</Label><p className="font-medium break-all">{selectedDoc.file_name}</p></div>
                <div><Label className="text-slate-500">Size</Label><p>{formatFileSize(selectedDoc.file_size)}</p></div>
                <div><Label className="text-slate-500">Document Type</Label><p>{selectedDoc.document_type_name || selectedDoc.document_type_code}</p></div>
                <div><Label className="text-slate-500">Version</Label><p>v{selectedDoc.version}</p></div>
                <div><Label className="text-slate-500">Entity</Label><p>{selectedDoc.linked_entity_type}: {selectedDoc.linked_entity_id}</p></div>
                <div><Label className="text-slate-500">Status</Label><p>{selectedDoc.status} {selectedDoc.is_expired && '(Expired)'}</p></div>
                <div><Label className="text-slate-500">Uploaded By</Label><p>{selectedDoc.uploaded_by_email}</p></div>
                <div><Label className="text-slate-500">Uploaded At</Label><p>{new Date(selectedDoc.uploaded_at).toLocaleString()}</p></div>
              </div>
              {selectedDoc.expires_at && (
                <div>
                  <Label className="text-slate-500">Expires At</Label>
                  <p className={selectedDoc.is_expired ? 'text-red-600' : ''}>{new Date(selectedDoc.expires_at).toLocaleDateString()}</p>
                </div>
              )}
              {selectedDoc.notes && (
                <div><Label className="text-slate-500">Notes</Label><p className="text-sm">{selectedDoc.notes}</p></div>
              )}
              {selectedDoc.archived_reason && (
                <div><Label className="text-slate-500">Archive Reason</Label><p className="text-sm">{selectedDoc.archived_reason}</p></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Modal */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Document</DialogTitle>
            <DialogDescription>This will archive the document. Requires a reason.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason (min 10 characters)</Label>
            <Textarea value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} rows={3} placeholder="Enter reason for archiving..." data-testid="archive-reason" />
            <p className="text-xs text-slate-500 mt-1">{archiveReason.length}/10 characters minimum</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveModalOpen(false)}>Cancel</Button>
            <Button onClick={handleArchive} disabled={submitting || archiveReason.length < 10} variant="destructive" data-testid="archive-submit">
              {submitting ? 'Archiving...' : 'Archive Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}

export default DocumentLibraryPage;
